import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/lib/siteData";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-slate-200"
          : "bg-white/60 border-b border-transparent"
      }`}
    >
      <div className="vd-container flex items-center justify-between h-32 md:h-44">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src={COMPANY.logoUrl}
            alt="VeriData Global — Trusted, Timely, Thorough"
            className="h-28 md:h-44 w-auto object-contain transition-transform group-hover:scale-[1.02]"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `relative px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-slate-900"
                    : "text-slate-600 hover:text-slate-900"
                }`
              }
            >
              {({ isActive }) => (
                <span className="inline-flex flex-col">
                  {link.label}
                  <span
                    className={`mt-1 h-[2px] transition-all ${
                      isActive ? "bg-teal-600 w-full" : "bg-transparent w-0"
                    }`}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors shadow-sm"
          >
            Request a Quote
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 -mr-2 text-slate-900"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="vd-container py-4 flex flex-col">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-mobile-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 text-base font-medium border-b border-slate-100 ${
                    isActive ? "text-teal-700" : "text-slate-800"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-mobile-cta"
              className="mt-4 inline-flex justify-center items-center gap-2 bg-orange-600 text-white font-medium px-5 py-3 rounded-md"
            >
              Request a Quote
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
